import Tribute from 'tributejs';
import 'tributejs/dist/tribute.css';

const ZERO_WIDTH_SPACE = '​';

/* Tags are identified by the canonical key the server normalises to, never by the `name` attribute
   as written: that attribute is a display string and the two sides that write it disagree on its
   case — Ruby's create_span uses String#capitalize ("Applicant full name") where the pickers
   title-case ("Applicant First Name"), and both spellings sit in the data. Comparing the raw
   strings splits one merge field into two groups, so a tag inserted beside one that came from a
   template counts as a different tag. */
export const tagKeyOf = name => (name || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[{}]/g, '')
  .replace(/\s+/g, '_');

/* The zero-width space the picker appends is stripped, but ordinary spacing is kept: this used to
   match [ZERO_WIDTH_SPACE + literal space] and so deleted every space in the value -- "Alex Smith"
   was adopted as "AlexSmith". Only single-word values were ever tested, which hid it. */
const visibleTextOf = el => el.innerText
  .replace(new RegExp(ZERO_WIDTH_SPACE, 'g'), ' ')
  .replace(/\s+/g, ' ')
  .trim();

/* Every occurrence of a tag in one document is backed by a single merge field, so they all have to
   read the same value. The picker's values are resolved once when the editor boots, so a tag
   inserted after someone edited an existing occurrence carried the pre-edit value and the document
   ended up showing two different texts for one key. Adopt what is already in the document instead,
   when there is anything worth adopting. */
export const valueAlreadyInDocument = (editorEl, key) => {
  if (!editorEl) return null;

  const wanted = tagKeyOf(key);
  if (!wanted) return null; // else it would match every span that carries no name

  /* An unfilled tag shows its own name as its text — Ruby's create_span writes name == content —
     so adopting that text would insert a placeholder dressed up as a value: the span would be
     classed tag-filled, and server-side process_span compares `value == name` exactly, so against
     a differently-cased name the placeholder would persist as a real tag_value and the red
     unfilled marker would vanish. Take the first occurrence that carries a genuine value. */
  const value = Array.from(editorEl.querySelectorAll('.fr-deletable.fr-tribute'))
    .filter(el => tagKeyOf(el.getAttribute('name')) === wanted)
    .map(visibleTextOf)
    .find(text => text.length && tagKeyOf(text) !== wanted);

  return value || null;
};

/* A tag reaches the document from two places — the tribute picker ({ while typing) and the tags
   dropdown in the toolbar. Both build the same span, so they share one builder: when they had a
   copy each, only the picker learned to adopt the document's value and the dropdown kept inserting
   the value resolved at boot.

   `bare` is the single-contract case, where the recruiter fills the tag on a later step and the
   span carries the key rather than a value. */
export const tagSpanFor = (editorEl, key, resolvedValue, { bare = false } = {}) => {
  const inDocument = valueAlreadyInDocument(editorEl, key);
  const span = (classes, text) => (
    `<span class='fr-deletable fr-tribute${classes}' contenteditable='false' name='${key}'>${text}</span>${ZERO_WIDTH_SPACE} &nbsp`
  );

  if (bare) {
    return inDocument ? span(' tag-filled', inDocument) : span('', key);
  }

  let tagValue = (typeof resolvedValue === 'string' && resolvedValue !== '') ? resolvedValue : key;
  if (inDocument) tagValue = inDocument;

  return span(tagValue === key ? ' tag-error-color' : ' tag-filled', tagValue);
};

const applyDynamicTagging = (tags, prefilledTags, editorEl = null) => {
  const isSingleContract = localStorage.getItem('singleContract') === 'true';
  const dynamicValues = (isSingleContract ? tags : prefilledTags)
    .map(item => {
      const key = item.name ? item.name.slice(1, -1) : item.slice(1, -1);
      const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, match => match.toUpperCase());
      return { key: formattedKey, value: item.value || item };
    });

  const tribute = new Tribute({
    values: dynamicValues,
    trigger: '{',
    selectTemplate(item) {
      return tagSpanFor(editorEl, item.original.key, item.original.value, {
        bare: localStorage.getItem('singleContract') === 'true'
      });
    }
  });

  return tribute;
};

export default applyDynamicTagging;
